/**
 * 日報の入力検証と運用警告（ガイドライン §0 / §7.2 / §11、運用マニュアル §3.3 / §4）
 *
 * error   … 提出できない（入力を直す必要がある）
 * warning … 提出はできるが、承認者・管理部が内容を確認する
 */
import { HOURLY_LEAVE_DAILY_LIMIT_HOURS, LONG_ATTENDANCE_WARN_MINUTES, BREAK_RULE, } from './constants.js';
import { firstDeparture, lastArrival } from './daily.js';
import { formatDuration } from './time.js';
import { WORKED_TYPES, } from './types.js';
/**
 * 警告・エラーの種別名。画面の絞り込みや一覧の見出しに使う（内部コードをそのまま出さない）。
 * 未知のコードは issueLabel() がコードのまま返す
 */
export const ISSUE_LABELS = {
    EXCLUSIVE_MORNING: '直行と出社の重複',
    EXCLUSIVE_EVENING: '退社と直帰の重複',
    WORK_TIME_ORDER: '始業・終業の前後関係',
    DIRECT_START_ORDER: '直行時刻の前後関係',
    DIRECT_HOME_ORDER: '直帰時刻の前後関係',
    OFFICE_IN_ORDER: '出社時刻の前後関係',
    OFFICE_OUT_ORDER: '退社時刻の前後関係',
    TRAVEL_ORDER: '移動時刻の前後関係',
    SPAN_ORDER: '出発・到着の前後関係',
    TIME_REQUIRED: '勤務時刻の未入力',
    WORK_TIME_REQUIRED: '始業・終業の未入力',
    TASK_NOTE_REQUIRED: '業務内容の未入力',
    PATTERN_UNKNOWN: '業務パターン不明',
    TIME_ON_NON_WORK_DAY: '休暇日の時刻入力',
    HOLIDAY_TREATMENT_MISSING: '休日出勤の取扱い未選択',
    HOLIDAY_BREAK_MISSING: '休日の休憩未入力',
    HOURLY_LEAVE_UNIT: '時間単位年休の単位',
    HOURLY_LEAVE_DAILY_LIMIT: '時間単位年休の上限',
    BREAK_NEGATIVE: '休憩の値',
    BREAK_OVERRIDE_EXCEEDS_TOTAL: '休憩修正が合計を超過',
    BREAK_SHORTAGE: '休憩不足',
    LONG_ATTENDANCE: '長時間拘束',
    MISSING_ENTRY: '未入力',
};
export function issueLabel(code) {
    return ISSUE_LABELS[code] ?? code;
}
/** 時刻の入力がまったくない日か */
export function hasNoTimeInput(input) {
    return (input.directStart === null &&
        input.officeIn === null &&
        input.workStart === null &&
        input.workEnd === null &&
        input.officeOut === null &&
        input.directHome === null);
}
/**
 * 日報1件分を検証する。
 * computed を渡すと、休憩不足・長時間拘束など計算結果に基づく警告も併せて返す。
 */
export function validateDaily(input, computed) {
    const issues = [];
    const isWorked = WORKED_TYPES.includes(input.workType);
    // 排他ルール（ガイドライン §7.2）
    if (input.directStart !== null && input.officeIn !== null) {
        issues.push({
            severity: 'error',
            code: 'EXCLUSIVE_MORNING',
            message: '①直行時刻と②出社時刻は同じ日に両方入力できません。どちらか一方にしてください。',
            field: 'officeIn',
        });
    }
    if (input.officeOut !== null && input.directHome !== null) {
        issues.push({
            severity: 'error',
            code: 'EXCLUSIVE_EVENING',
            message: '⑤退社時刻と⑥直帰時刻は同じ日に両方入力できません。どちらか一方にしてください。',
            field: 'directHome',
        });
    }
    // 時刻の前後関係
    if (input.workStart !== null && input.workEnd !== null && input.workEnd <= input.workStart) {
        issues.push({
            severity: 'error',
            code: 'WORK_TIME_ORDER',
            message: '④終業時刻は③始業時刻より後にしてください（跨日は25:00のように入力します）。',
            field: 'workEnd',
        });
    }
    if (input.directStart !== null &&
        input.workStart !== null &&
        input.directStart > input.workStart) {
        issues.push({
            severity: 'error',
            code: 'DIRECT_START_ORDER',
            message: '①直行時刻は③始業時刻より前にしてください。',
            field: 'directStart',
        });
    }
    if (input.directHome !== null && input.workEnd !== null && input.directHome < input.workEnd) {
        issues.push({
            severity: 'error',
            code: 'DIRECT_HOME_ORDER',
            message: '⑥直帰時刻は④終業時刻より後にしてください。',
            field: 'directHome',
        });
    }
    if (input.officeIn !== null && input.workStart !== null && input.officeIn > input.workStart) {
        issues.push({
            severity: 'warning',
            code: 'OFFICE_IN_ORDER',
            message: '②出社時刻が③始業時刻より後になっています。入力内容を確認してください。',
            field: 'officeIn',
        });
    }
    if (input.officeOut !== null && input.workEnd !== null && input.officeOut < input.workEnd) {
        issues.push({
            severity: 'warning',
            code: 'OFFICE_OUT_ORDER',
            message: '⑤退社時刻が④終業時刻より前になっています。入力内容を確認してください。',
            field: 'officeOut',
        });
    }
    if (input.directStart !== null &&
        input.directHome !== null &&
        input.directHome <= input.directStart) {
        issues.push({
            severity: 'error',
            code: 'TRAVEL_ORDER',
            message: '⑥直帰時刻は①直行時刻より後にしてください。',
            field: 'directHome',
        });
    }
    // 勤務種別と入力の整合
    if (isWorked) {
        const pattern = computed?.pattern ?? null;
        if (hasNoTimeInput(input)) {
            issues.push({
                severity: 'error',
                code: 'TIME_REQUIRED',
                message: '勤務種別が「' + input.workType + '」のため、勤務時刻を入力してください。',
                field: 'workStart',
            });
        }
        else if (pattern !== 'E' && (input.workStart === null || input.workEnd === null)) {
            issues.push({
                severity: 'error',
                code: 'WORK_TIME_REQUIRED',
                message: '③始業時刻・④終業時刻を入力してください（移動のみの日は①直行と⑥直帰だけを入力します）。',
                field: input.workStart === null ? 'workStart' : 'workEnd',
            });
        }
        if (input.taskNote.trim() === '') {
            issues.push({
                severity: 'warning',
                code: 'TASK_NOTE_REQUIRED',
                message: '業務内容が未入力です。',
                field: 'taskNote',
            });
        }
        if (pattern === null && !hasNoTimeInput(input)) {
            issues.push({
                severity: 'warning',
                code: 'PATTERN_UNKNOWN',
                message: '入力パターンを判定できません。朝は①直行または②出社、帰りは⑤退社または⑥直帰を入力してください。',
            });
        }
    }
    else if (!hasNoTimeInput(input) && input.workType !== '時間年休') {
        issues.push({
            severity: 'warning',
            code: 'TIME_ON_NON_WORK_DAY',
            message: `勤務種別が「${input.workType}」ですが勤務時刻が入力されています。内容を確認してください。`,
            field: 'workType',
        });
    }
    // 休日出勤の運用（ガイドライン §9.3 / 運用マニュアル §3.3）
    if (input.workType === '休日出勤') {
        if (input.holidayWorkTreatment === null) {
            issues.push({
                severity: 'warning',
                code: 'HOLIDAY_TREATMENT_MISSING',
                message: '休日出勤の取扱い（振替休日／代休／残業手当希望）が未選択です。',
                field: 'holidayWorkTreatment',
            });
        }
        if (input.breakTotalMinutes === null) {
            issues.push({
                severity: 'warning',
                code: 'HOLIDAY_BREAK_MISSING',
                message: '休日出勤は休憩時間が自動計算されません。実際に取得した休憩を入力してください。',
                field: 'breakTotalMinutes',
            });
        }
    }
    // 時間単位年休
    if (input.hourlyLeaveHours < 0 || !Number.isInteger(input.hourlyLeaveHours)) {
        issues.push({
            severity: 'error',
            code: 'HOURLY_LEAVE_UNIT',
            message: '⑧時間単位年休は1時間単位の整数で入力してください。',
            field: 'hourlyLeaveHours',
        });
    }
    else if (input.hourlyLeaveHours > HOURLY_LEAVE_DAILY_LIMIT_HOURS) {
        issues.push({
            severity: 'error',
            code: 'HOURLY_LEAVE_DAILY_LIMIT',
            message: `⑧時間単位年休は1日あたり${HOURLY_LEAVE_DAILY_LIMIT_HOURS}時間までです。`,
            field: 'hourlyLeaveHours',
        });
    }
    // 休憩
    if (input.breakTotalMinutes !== null && input.breakTotalMinutes < 0) {
        issues.push({
            severity: 'error',
            code: 'BREAK_NEGATIVE',
            message: '⑦休憩時間に負の値は入力できません。',
            field: 'breakTotalMinutes',
        });
    }
    if (input.breakOverrideMinutes !== null &&
        input.breakTotalMinutes !== null &&
        input.breakOverrideMinutes > input.breakTotalMinutes) {
        issues.push({
            severity: 'error',
            code: 'BREAK_OVERRIDE_EXCEEDS_TOTAL',
            message: '休憩修正（実働中の休憩）は⑦休憩合計を超えられません。',
            field: 'breakOverrideMinutes',
        });
    }
    if (computed) {
        if (computed.breakShortage) {
            const required = computed.workMinutes > BREAK_RULE.over8hThresholdMinutes
                ? BREAK_RULE.over8hRequiredMinutes
                : BREAK_RULE.over6hRequiredMinutes;
            issues.push({
                severity: 'warning',
                code: 'BREAK_SHORTAGE',
                message: `休憩不足です。実働${formatDuration(computed.workMinutes)}には実働中の休憩が${required}分以上必要です（労働基準法第34条）。`,
                field: 'breakTotalMinutes',
            });
        }
        if (computed.attendanceSpanMinutes > LONG_ATTENDANCE_WARN_MINUTES) {
            issues.push({
                severity: 'warning',
                code: 'LONG_ATTENDANCE',
                message: `拘束時間が${formatDuration(computed.attendanceSpanMinutes)}です。内容を確認してください。`,
            });
        }
    }
    const span = spanMinutes(input);
    if (span !== null && span <= 0) {
        issues.push({
            severity: 'error',
            code: 'SPAN_ORDER',
            message: '最後の到着時刻が最初の出発時刻より前になっています。',
        });
    }
    return issues;
}
function spanMinutes(input) {
    const start = firstDeparture(input);
    const end = lastArrival(input);
    if (start === null || end === null)
        return null;
    return end - start;
}
/** エラーが含まれるか（提出可否の判定） */
export function hasError(issues) {
    return issues.some((issue) => issue.severity === 'error');
}
/** 警告が含まれるか */
export function hasWarning(issues) {
    return issues.some((issue) => issue.severity === 'warning');
}
//# sourceMappingURL=validation.js.map